/**
 * FASE H — Pagina Provisioning Utenze `/admin/utenze` (solo super_admin).
 *
 * CRUD utenze: create, change role, suspend, reactivate, reset password, delete.
 * Ogni azione loggata backend in `admin_audit_log`.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, UserPlus, RefreshCw, Search, ShieldAlert, KeyRound,
  Pause, Play, Trash2, Loader2, Copy, CheckCircle2, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABEL = {
  super_admin: { label: "Super Admin", cls: "bg-red-100 text-red-800" },
  admin:       { label: "Admin",       cls: "bg-purple-100 text-purple-800" },
  dipendente:  { label: "Dipendente",  cls: "bg-emerald-100 text-emerald-800" },
  operatore:   { label: "Operatore",   cls: "bg-emerald-100 text-emerald-800" },
};
const STATUS_LABEL = {
  active:    { label: "Attivo",     cls: "bg-emerald-100 text-emerald-800" },
  suspended: { label: "Sospeso",    cls: "bg-amber-100 text-amber-800" },
  disabled:  { label: "Disabilitato", cls: "bg-slate-200 text-slate-600" },
};


function PasswordModal({ password, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!password) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          data-testid="password-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <h3 className="font-heading text-xl text-[#0F1F14] mb-3">Password temporanea</h3>
        <p className="text-sm text-slate-600 mb-4">
          Consegnala all'utente in modo sicuro (Signal / handshake fisico).
          Al primo login sarà richiesto il cambio.
        </p>
        <div className="bg-[#F7F5EE] border border-[#B9A268] rounded-lg p-4 flex items-center justify-between font-mono text-lg mb-4"
              data-testid="temp-password-display">
          {password}
          <button
            data-testid="copy-temp-password"
            onClick={() => {
              navigator.clipboard.writeText(password);
              setCopied(true); setTimeout(() => setCopied(false), 2000);
            }}
            className="p-2 hover:bg-white rounded">
            {copied ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Copy className="w-5 h-5" />}
          </button>
        </div>
        <button onClick={onClose}
                 className="w-full py-2 bg-[#0F1F14] text-white rounded-lg hover:bg-[#1F3A28]">
          Chiudi
        </button>
      </div>
    </div>
  );
}


function CreateUserModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", name: "", role: "dipendente", rui_number: "" });
  const [loading, setLoading] = useState(false);
  if (!open) return null;

  const submit = async () => {
    if (!form.email || !form.name) { toast.error("Email e nome richiesti"); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/users`, form, { withCredentials: true });
      toast.success(`Utente ${r.data.email} creato`);
      onCreated?.(r.data.password_temp);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          data-testid="create-user-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl text-[#0F1F14]">Nuovo utente</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase text-slate-500">Email</label>
            <input type="email" value={form.email}
                    data-testid="new-user-email"
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase text-slate-500">Nome completo</label>
            <input value={form.name}
                    data-testid="new-user-name"
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase text-slate-500">Ruolo</label>
            <select value={form.role}
                     data-testid="new-user-role"
                     onChange={(e) => setForm({ ...form, role: e.target.value })}
                     className="mt-1 w-full px-3 py-2 border rounded-lg text-sm">
              <option value="dipendente">Dipendente</option>
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase text-slate-500">RUI (opz.)</label>
            <input value={form.rui_number}
                    data-testid="new-user-rui"
                    onChange={(e) => setForm({ ...form, rui_number: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose}
                   className="flex-1 py-2 border border-slate-300 rounded-lg text-sm">
            Annulla
          </button>
          <button onClick={submit} disabled={loading}
                   data-testid="submit-new-user"
                   className="flex-1 py-2 bg-[#0F1F14] text-white rounded-lg text-sm disabled:opacity-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Crea utente"}
          </button>
        </div>
      </div>
    </div>
  );
}


export default function AdminUsersPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (roleFilter) params.set("role", roleFilter);
      if (statusFilter) params.set("status", statusFilter);
      const r = await axios.get(`${API}/admin/users?${params}`, { withCredentials: true });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error("Errore caricamento utenti");
    } finally { setLoading(false); }
  }, [q, roleFilter, statusFilter]);
  useEffect(() => { load(); }, [load]);

  const doAction = async (uid, action, path = null) => {
    try {
      const url = path || `${API}/admin/users/${uid}/${action}`;
      const method = action === "delete" ? "delete" : "post";
      const r = await axios[method](url, {}, { withCredentials: true });
      toast.success(`${action} OK`);
      if (r.data?.password_temp) setTempPassword(r.data.password_temp);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore azione");
    }
  };

  const changeRole = async (uid, newRole) => {
    try {
      await axios.patch(`${API}/admin/users/${uid}/role`,
                            { role: newRole }, { withCredentials: true });
      toast.success("Ruolo aggiornato");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore cambio ruolo");
    }
  };

  return (
    <div className="p-6 max-w-full" data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <Link to="/admin/dashboard"
                 className="text-sm text-slate-500 hover:text-[#0F1F14] inline-flex items-center gap-1 mb-1">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <h1 className="font-heading text-3xl text-[#0F1F14]">
            Provisioning Utenze
          </h1>
          <p className="text-sm text-[#4A5E51] mt-1">
            {total} utenti · gestione ruoli, sospensione, reset password
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setOpenCreate(true)}
                   data-testid="btn-new-user"
                   className="inline-flex items-center gap-2 px-4 py-2 bg-[#0F1F14] text-white rounded-lg text-sm hover:bg-[#1F3A28]">
            <UserPlus className="w-4 h-4" /> Nuovo utente
          </button>
          <button onClick={load} className="p-2 hover:bg-slate-100 rounded-lg">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Filtri */}
      <div className="bg-white border border-[#E5E2DB] rounded-2xl p-3 mb-4 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                  data-testid="users-search"
                  placeholder="Cerca email o nome…"
                  className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" />
        </div>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
                 data-testid="users-role-filter"
                 className="px-3 py-2 border rounded-lg text-sm">
          <option value="">Tutti i ruoli</option>
          <option value="super_admin">Super Admin</option>
          <option value="admin">Admin</option>
          <option value="dipendente">Dipendente</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                 data-testid="users-status-filter"
                 className="px-3 py-2 border rounded-lg text-sm">
          <option value="">Tutti gli stati</option>
          <option value="active">Attivo</option>
          <option value="suspended">Sospeso</option>
          <option value="disabled">Disabilitato</option>
        </select>
      </div>

      {/* Tabella */}
      <div className="bg-white border border-[#E5E2DB] rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-[#F7F5EE] text-xs uppercase text-slate-600">
            <tr>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">Nome</th>
              <th className="px-4 py-3 text-left">Ruolo</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Ultimo accesso</th>
              <th className="px-4 py-3 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => {
              const uid = u.id || u._id;
              const rl = ROLE_LABEL[u.role] || { label: u.role, cls: "bg-slate-100 text-slate-700" };
              const sl = STATUS_LABEL[u.status || "active"] || { label: u.status || "—", cls: "bg-slate-100 text-slate-700" };
              return (
                <tr key={uid} className="border-t border-[#E5E2DB] hover:bg-[#FAF9F4]"
                     data-testid={`user-row-${uid}`}>
                  <td className="px-4 py-2 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-2">{u.name || "—"}</td>
                  <td className="px-4 py-2">
                    <select value={u.role || ""}
                             data-testid={`role-select-${uid}`}
                             onChange={(e) => changeRole(uid, e.target.value)}
                             className={`text-[11px] font-semibold px-2 py-1 rounded ${rl.cls} border-0`}>
                      <option value="super_admin">Super Admin</option>
                      <option value="admin">Admin</option>
                      <option value="dipendente">Dipendente</option>
                    </select>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${sl.cls}`}>
                      {sl.label}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-[11px] text-slate-500">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString("it-IT") : "—"}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => doAction(uid, "reset-password")}
                               data-testid={`reset-pwd-${uid}`}
                               title="Reset password"
                               className="p-1.5 hover:bg-slate-100 rounded">
                        <KeyRound className="w-3.5 h-3.5" />
                      </button>
                      {u.status !== "suspended" ? (
                        <button onClick={() => doAction(uid, "suspend")}
                                 data-testid={`suspend-${uid}`}
                                 title="Sospendi"
                                 className="p-1.5 hover:bg-amber-100 text-amber-700 rounded">
                          <Pause className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        <button onClick={() => doAction(uid, "reactivate")}
                                 data-testid={`reactivate-${uid}`}
                                 title="Riattiva"
                                 className="p-1.5 hover:bg-emerald-100 text-emerald-700 rounded">
                          <Play className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button onClick={() => {
                                if (window.confirm(`Eliminare ${u.email}?`)) doAction(uid, "delete");
                              }}
                               data-testid={`delete-${uid}`}
                               title="Elimina (soft)"
                               className="p-1.5 hover:bg-red-100 text-red-700 rounded">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-slate-400 italic">
                  Nessun utente
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CreateUserModal open={openCreate}
                          onClose={() => setOpenCreate(false)}
                          onCreated={(pwd) => setTempPassword(pwd)} />
      <PasswordModal password={tempPassword}
                        onClose={() => setTempPassword(null)} />
    </div>
  );
}
